import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Clock, Users, Wallet, Download, RefreshCw, TrendingUp, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, Card, Button, Select, Badge, Skeleton } from "@/components/ui-kit";
import { DatePicker } from "@/components/DatePicker";
import { formatIDR } from "@/lib/format";

/**
 * Iter v34ay — Laporan Lembur (Overtime Report).
 * Aggregasi total jam lembur & upah lembur per karyawan dalam rentang tanggal.
 * Filter: rentang tanggal (default: bulan berjalan), departemen, karyawan.
 * Export CSV.
 */
export default function OvertimeReport() {
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
  const fmt = (d) => d.toISOString().slice(0, 10);

  const [fromDate, setFromDate] = useState(fmt(firstDay));
  const [toDate, setToDate] = useState(fmt(today));
  const [department, setDepartment] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/department-categories").then((r) => setDepartments(r.data || [])).catch(() => setDepartments([]));
    api.get("/employees").then((r) => setEmployees(r.data || [])).catch(() => setEmployees([]));
  }, []);

  const load = async () => {
    if (!fromDate || !toDate) return;
    setLoading(true);
    try {
      const params = { from: fromDate, to: toDate };
      if (department) params.department = department;
      if (employeeId) params.employee_id = employeeId;
      const r = await api.get("/overtime/report", { params });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat laporan lembur");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (fromDate && toDate) load(); }, [fromDate, toDate, department, employeeId]);

  const filteredEmployees = useMemo(() => {
    if (!department) return employees;
    // Iter v34b6 — Match department_category primary + department legacy fallback.
    return employees.filter((e) => (e.department_category || e.department) === department);
  }, [employees, department]);

  const exportCSV = () => {
    if (!data?.rows?.length) { toast.error("Tidak ada data untuk diekspor"); return; }
    const sep = ",";
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const header = ["Kode", "Nama", "Departemen", "Posisi", "Hari Lembur", "Total Jam Lembur", "Tarif/Jam (Rp)", "Total Upah (Rp)"].map(esc).join(sep);
    const rows = data.rows.map((r) => [
      r.employee_code, r.name, r.department || "-", r.position || "-",
      r.days_with_overtime, r.total_overtime_hours, r.effective_rate, r.total_upah_lembur,
    ].map(esc).join(sep));
    const totalRow = ["", "TOTAL", "", "", "", data.total_overtime_hours, "", data.total_upah_lembur].map(esc).join(sep);
    const csv = [header, ...rows, totalRow].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Laporan_Lembur_${fromDate}_sampai_${toDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const setThisMonth = () => {
    const d = new Date();
    setFromDate(fmt(new Date(d.getFullYear(), d.getMonth(), 1)));
    setToDate(fmt(d));
  };
  const setLastMonth = () => {
    const d = new Date();
    const first = new Date(d.getFullYear(), d.getMonth() - 1, 1);
    const last = new Date(d.getFullYear(), d.getMonth(), 0);
    setFromDate(fmt(first));
    setToDate(fmt(last));
  };

  return (
    <div className="min-h-full bg-paper p-4 md:p-6 lg:p-8">
      <div className="max-w-[1500px] mx-auto space-y-6">
        <PageHeader
          overline="HR · Absensi"
          title="Laporan Lembur"
          description="Rekap total jam lembur & upah lembur per karyawan berdasarkan rentang tanggal."
          action={
            <Button onClick={exportCSV} variant="secondary" data-testid="ot-export-csv">
              <Download className="w-3.5 h-3.5"/> Export CSV
            </Button>
          }
        />

        {/* Filter bar */}
        <Card className="p-4 md:p-5">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-stone-600 mb-1.5">Dari Tanggal</label>
              <DatePicker value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="ot-from" />
            </div>
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-stone-600 mb-1.5">Sampai Tanggal</label>
              <DatePicker value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="ot-to" />
            </div>
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-stone-600 mb-1.5">Departemen</label>
              <Select value={department} onChange={(e) => { setDepartment(e.target.value); setEmployeeId(""); }} data-testid="ot-department">
                <option value="">Semua Departemen</option>
                {departments.map((d) => <option key={d.id || d.name} value={d.name}>{d.name}</option>)}
              </Select>
            </div>
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-stone-600 mb-1.5">Karyawan</label>
              <Select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} data-testid="ot-employee">
                <option value="">Semua Karyawan</option>
                {filteredEmployees.map((e) => <option key={e.id} value={e.id}>{e.name} {e.employee_code ? `(${e.employee_code})` : ""}</option>)}
              </Select>
            </div>
            <div className="flex gap-2">
              <Button onClick={setThisMonth} variant="secondary" className="!px-3" data-testid="ot-this-month">Bulan Ini</Button>
              <Button onClick={setLastMonth} variant="secondary" className="!px-3" data-testid="ot-last-month">Bulan Lalu</Button>
              <Button onClick={load} variant="secondary" className="!px-3" data-testid="ot-refresh"><RefreshCw className="h-3.5 w-3.5"/></Button>
            </div>
          </div>
        </Card>

        {/* Summary cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <SummaryCard icon={Users} label="Karyawan Lembur" value={data?.total_employees ?? 0} loading={loading} testid="ot-sum-employees"/>
          <SummaryCard icon={Clock} label="Total Jam Lembur" value={data ? `${data.total_overtime_hours.toFixed(2)} jam` : "0 jam"} loading={loading} testid="ot-sum-hours"/>
          <SummaryCard icon={Wallet} label="Total Upah Lembur" value={data ? formatIDR(data.total_upah_lembur) : formatIDR(0)} loading={loading} tone="blue" testid="ot-sum-upah"/>
          <SummaryCard icon={TrendingUp} label="Default Tarif/Jam" value={data ? formatIDR(data.default_rate) : formatIDR(0)} loading={loading} testid="ot-sum-default"/>
        </div>

        {/* Table */}
        <Card className="p-4 md:p-5">
          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full"/>)}
            </div>
          ) : (data?.rows || []).length === 0 ? (
            <div className="text-center py-16 text-stone-400" data-testid="ot-empty">
              <Sparkles className="w-10 h-10 mx-auto mb-3 text-stone-300"/>
              <p>Tidak ada data lembur di rentang tanggal ini.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="ot-table">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-widest text-stone-500 border-b border-stone-200">
                    <th className="py-3 px-2">#</th>
                    <th className="py-3 px-2">Kode</th>
                    <th className="py-3 px-2">Nama Karyawan</th>
                    <th className="py-3 px-2">Departemen</th>
                    <th className="py-3 px-2 text-right">Hari</th>
                    <th className="py-3 px-2 text-right">Total Jam</th>
                    <th className="py-3 px-2 text-right">Tarif/Jam</th>
                    <th className="py-3 px-2 text-right">Total Upah</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r, i) => (
                    <tr key={r.employee_id} className="border-b border-stone-100 hover:bg-stone-50/50" data-testid={`ot-row-${r.employee_id}`}>
                      <td className="py-3 px-2 text-stone-500">{i + 1}</td>
                      <td className="py-3 px-2 font-mono-num text-xs text-stone-600">{r.employee_code || "-"}</td>
                      <td className="py-3 px-2 font-medium text-stone-900">{r.name}</td>
                      <td className="py-3 px-2 text-stone-600">{r.department || "-"}</td>
                      <td className="py-3 px-2 text-right font-mono-num">{r.days_with_overtime}</td>
                      <td className="py-3 px-2 text-right font-mono-num font-medium">{r.total_overtime_hours.toFixed(2)}</td>
                      <td className="py-3 px-2 text-right font-mono-num">
                        {formatIDR(r.effective_rate)}
                        {r.uses_default_rate && <Badge tone="stone" className="ml-1 text-[9px]">default</Badge>}
                      </td>
                      <td className="py-3 px-2 text-right font-mono-num font-semibold text-blue-800">{formatIDR(r.total_upah_lembur)}</td>
                    </tr>
                  ))}
                  <tr className="bg-blue-50/60 font-semibold">
                    <td colSpan={5} className="py-3 px-2 text-right text-blue-800 uppercase tracking-wider text-xs">Total</td>
                    <td className="py-3 px-2 text-right font-mono-num text-blue-800">{data.total_overtime_hours.toFixed(2)}</td>
                    <td></td>
                    <td className="py-3 px-2 text-right font-mono-num text-blue-800" data-testid="ot-grand-total">{formatIDR(data.total_upah_lembur)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, loading, tone, testid }) {
  return (
    <div className={`rounded-2xl border p-4 ${tone === "blue" ? "bg-gradient-to-br from-blue-50 to-white border-blue-100" : "bg-white border-stone-200/70"}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-stone-500 font-semibold">
        <Icon className={`h-3.5 w-3.5 ${tone === "blue" ? "text-blue-600" : "text-stone-400"}`}/> {label}
      </div>
      {loading ? (
        <Skeleton className="h-8 w-24 mt-2"/>
      ) : (
        <div className={`font-display text-2xl mt-2 font-mono-num ${tone === "blue" ? "text-blue-800" : "text-stone-900"}`}>{value}</div>
      )}
    </div>
  );
}
